import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api as http } from '../lib/api.ts';
import { insertSubPage } from './blocks.tsx';
import type { EditorApi } from './editor.tsx';
import { caretOffset, splitSpans } from './rich.tsx';
import { createPage, getState, reloadDoc, setCaret } from './store.ts';
import type { Block, BlockType, Database } from './types.ts';

type Entry = {
  label: string;
  hint: string;
  icon: string;
  keywords?: string;
  run: (block: Block, api: EditorApi) => void | Promise<void>;
};

const basic = (label: string, icon: string, type: BlockType, hint: string, props?: Record<string, any>): Entry => ({
  label, icon, hint,
  run: (block, api) => api.setType(block.id, type, props),
});

const ENTRIES: Entry[] = [
  basic('Text', '¶', 'text', 'Just start writing'),
  basic('Heading 1', 'H1', 'h1', 'Big section heading'),
  basic('Heading 2', 'H2', 'h2', 'Medium section heading'),
  basic('Heading 3', 'H3', 'h3', 'Small section heading'),
  basic('Bulleted list', '•', 'bulleted', 'A simple bulleted list'),
  basic('Numbered list', '1.', 'numbered', 'A list with numbering'),
  { ...basic('To-do list', '☑', 'todo', 'Track tasks with a checkbox', { checked: false }), keywords: 'task checkbox' },
  basic('Toggle list', '▸', 'toggle', 'Hide things inside', { open: true }),
  basic('Quote', '❝', 'quote', 'Capture a quote'),
  { ...basic('Callout', '💡', 'callout', 'Make writing stand out', { icon: '💡' }), keywords: 'note box writing' },
  basic('Code', '{}', 'code', 'Code with a language', { language: 'text' }),
  {
    label: 'Divider', icon: '—', hint: 'A line to separate things', keywords: 'line separator hr',
    run: (block, api) => {
      api.setType(block.id, 'divider');
      api.insertAfter(block, 'text');
    },
  },
  {
    label: 'Page', icon: '📄', hint: 'A new page inside this one', keywords: 'sub subpage child',
    run: (block, api) => insertSubPage(block, api),
  },
  {
    label: 'Table', icon: '▦', hint: 'A database shown as a table', keywords: 'database grid spreadsheet',
    run: (block, api) => insertDatabase(block, api, 'table'),
  },
  {
    label: 'Board', icon: '▥', hint: 'A database shown as a kanban board', keywords: 'kanban database',
    run: (block, api) => insertDatabase(block, api, 'board'),
  },
  {
    label: 'Image', icon: '🖼', hint: 'Upload or link a picture', keywords: 'picture photo upload',
    run: (block, api) => api.setType(block.id, 'image'),
  },
  {
    label: 'File', icon: '📎', hint: 'Upload any file', keywords: 'attachment upload document',
    run: (block, api) => api.setType(block.id, 'file'),
  },
  {
    label: 'Bookmark', icon: '🔗', hint: 'A link with its title', keywords: 'link url web',
    run: (block, api) => api.setType(block.id, 'bookmark'),
  },
];

async function insertDatabase(block: Block, api: EditorApi, kind: 'table' | 'board'): Promise<void> {
  const parentId = getState().doc?.page.id ?? null;
  const page = await createPage({ parentId, title: 'Untitled', isDatabase: true, open: false });
  if (!page) return;
  if (kind === 'board') {
    const db = await http.get<Database>(`/api/db/${page.id}`);
    const status = db.props.find((p) => p.type === 'select');
    const view = db.views[0];
    if (view) {
      await http.patch(`/api/db/views/${view.id}`, { kind: 'board', group_by: status?.id ?? null, name: 'Board' });
    }
  }
  api.setType(block.id, 'db', { db_id: page.id });
  await reloadDoc();
}

export type SlashState = { blockId: string; rect: DOMRect } | null;

export function SlashMenu({
  state, api, onClose,
}: {
  state: SlashState;
  api: EditorApi;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  // Reset whenever the menu is opened afresh.
  useEffect(() => { setQuery(''); setActive(0); }, [state?.blockId, state?.rect?.top]);

  const matches = ENTRIES.filter((entry) => {
    if (!query) return true;
    const hay = `${entry.label} ${entry.keywords ?? ''}`.toLowerCase();
    return hay.includes(query.toLowerCase());
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !state) return;
    const box = el.getBoundingClientRect();
    const below = state.rect.bottom + 6;
    const fits = below + box.height < window.innerHeight - 8;
    setPos({
      x: Math.min(state.rect.left, window.innerWidth - box.width - 12),
      y: fits ? below : Math.max(8, state.rect.top - box.height - 6),
    });
  }, [state, matches.length]);

  useEffect(() => {
    if (!state) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => (a + 1) % Math.max(1, matches.length));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => (a - 1 + Math.max(1, matches.length)) % Math.max(1, matches.length));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const entry = matches[active];
        if (entry) pick(entry);
        else onClose();
        return;
      }
      if (e.key === 'Backspace') {
        // Backspacing past the `/` closes the menu rather than eating the line.
        if (!query) { onClose(); return; }
        setQuery((q) => q.slice(0, -1));
        return;
      }
      if (e.key === ' ') { onClose(); return; }
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        setQuery((q) => q + e.key);
      }
    };

    // Capture, so the editor never sees these keys while the menu is up.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [state, matches, active, query, onClose]);

  if (!state) return null;

  function pick(entry: Entry): void {
    const block = api.blocks.find((b) => b.id === state!.blockId);
    if (!block) { onClose(); return; }

    // Take the `/query` back out of the text before the block changes type.
    const el = document.querySelector<HTMLElement>(`[data-block="${block.id}"]`);
    const caret = el ? caretOffset(el) ?? 0 : 0;
    const remove = query.length + 1;
    const [head] = splitSpans(block.rich, Math.max(0, caret - remove));
    const [, tail] = splitSpans(block.rich, caret);
    const cleaned = [...head, ...tail];

    onClose();
    api.update(block.id, { rich: cleaned }, true);
    void Promise.resolve(entry.run({ ...block, rich: cleaned }, api)).then(() => {
      setCaret({ blockId: block.id, at: Math.max(0, caret - remove) });
    });
  }

  return createPortal(
    <div ref={ref} className="slash" style={{ left: pos.x, top: pos.y }} role="listbox" aria-label="Blocks">
      <div className="slash-head">{query ? `Blocks matching “${query}”` : 'Basic blocks'}</div>
      {matches.length ? matches.map((entry, i) => (
        <button
          key={entry.label}
          type="button"
          role="option"
          aria-selected={i === active}
          className={`slash-item${i === active ? ' on' : ''}`}
          onMouseEnter={() => setActive(i)}
          onMouseDown={(e) => { e.preventDefault(); pick(entry); }}
        >
          <span className="slash-icon" aria-hidden>{entry.icon}</span>
          <span className="slash-text">
            <span className="slash-label">{entry.label}</span>
            <span className="slash-hint">{entry.hint}</span>
          </span>
        </button>
      )) : (
        <div className="slash-none">No blocks match that.</div>
      )}
    </div>,
    document.body,
  );
}
